import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';
import TimeSlotAnalysisChart from '../../components/TimeSlotAnalysisChart';
import { utilBadgeStyle } from '../../utils/classroomUtilizationHelpers';

function getDefaultRange() {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 29);
  const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { startDate: fmt(start), endDate: fmt(end) };
}

function BucketTable({ title, bucket, dayCount, operatingHoursPerDay, dayType, onViewDetails }) {
  if (!bucket) return null;
  const { rooms, summary } = bucket;

  return (
    <div className="mb-10">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-lg font-bold text-gray-900">{title}</h3>
        <span className="text-xs text-gray-500">
          {dayCount} day{dayCount === 1 ? '' : 's'} in range &middot; {operatingHoursPerDay}h/day assumed operating hours
        </span>
      </div>

      {summary && (
        <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div className="rounded-lg border border-gray-200 bg-white p-3 text-center">
            <div className="text-lg font-bold text-gray-900">{summary.avgUtilizationPct ?? '—'}%</div>
            <div className="text-[11px] text-gray-500">Average utilization</div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-3 text-center">
            <div className="text-lg font-bold text-gray-900">{summary.totalBookedHours}h</div>
            <div className="text-[11px] text-gray-500">
              Total booked (Class: {summary.totalClassHours}h &middot; Event: {summary.totalEventHours}h)
            </div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-3 text-center">
            <div className="truncate text-sm font-bold text-emerald-700">{summary.mostUtilized?.name || '—'}</div>
            <div className="text-[11px] text-gray-500">Most utilized ({summary.mostUtilized?.utilizationPct ?? '—'}%)</div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-3 text-center">
            <div className="truncate text-sm font-bold text-red-700">{summary.leastUtilized?.name || '—'}</div>
            <div className="text-[11px] text-gray-500">Least utilized ({summary.leastUtilized?.utilizationPct ?? '—'}%)</div>
          </div>
        </div>
      )}

      {rooms.length === 0 ? (
        <p className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-center text-sm text-gray-500">
          No classrooms found.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
          <table className="w-full min-w-[720px] text-left text-xs">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50 uppercase tracking-wide text-gray-400">
                <th className="px-4 py-2">Room</th>
                <th className="px-4 py-2">Floor</th>
                <th className="px-4 py-2 text-center">Sessions</th>
                <th className="px-4 py-2 text-center">Class (h)</th>
                <th className="px-4 py-2 text-center">Event (h)</th>
                <th className="px-4 py-2 text-center">Booked (h)</th>
                <th className="px-4 py-2 text-center">Blocked (h)</th>
                <th className="px-4 py-2 text-center">Available (h)</th>
                <th className="px-4 py-2 text-center">Utilization</th>
                <th className="px-4 py-2 text-center">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rooms.map((r) => (
                <tr key={r.classroomId} className="hover:bg-gray-50">
                  <td className="px-4 py-2 font-medium text-gray-800">{r.name}</td>
                  <td className="px-4 py-2 text-gray-500">{r.floor}</td>
                  <td className="px-4 py-2 text-center text-gray-600">{r.sessionCount}</td>
                  <td className="px-4 py-2 text-center text-gray-600">{r.classHours}</td>
                  <td className="px-4 py-2 text-center text-gray-600">{r.eventHours}</td>
                  <td className="px-4 py-2 text-center font-semibold text-gray-700">{r.bookedHours}</td>
                  <td className="px-4 py-2 text-center text-gray-600">{r.blockedHours}</td>
                  <td className="px-4 py-2 text-center text-gray-600">{r.availableHours}</td>
                  <td className="px-4 py-2 text-center">
                    <span className="inline-block rounded-full px-2 py-1 font-bold" style={utilBadgeStyle(r.utilizationPct)}>
                      {r.utilizationPct === null ? '—' : `${r.utilizationPct}%`}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-center">
                    <button
                      type="button"
                      onClick={() => onViewDetails(r, dayType)}
                      className="rounded-md border border-indigo-300 bg-indigo-50 px-2 py-1 text-[11px] font-medium text-indigo-700 hover:bg-indigo-100"
                    >
                      View calculation
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <TimeSlotAnalysisChart
        analysis={bucket.timeSlotAnalysis}
        occupiedLabel="room-days"
        description="Hourly occupancy across every classroom — the % of room-days where any part of that hour was booked (a class or an event). Use this to spot peak hours worth relieving and quiet hours worth filling."
      />
    </div>
  );
}

export default function ClassroomUtilizationReport() {
  const navigate = useNavigate();
  const defaultRange = getDefaultRange();

  const [startDate, setStartDate] = useState(defaultRange.startDate);
  const [endDate, setEndDate] = useState(defaultRange.endDate);
  const [operatingHoursInput, setOperatingHoursInput] = useState('8.5');
  const [appliedOperatingHours, setAppliedOperatingHours] = useState(8.5);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await axiosInstance.get('/admin/dashboard/classroom-utilization', {
        params: { startDate, endDate, operatingHoursPerDay: appliedOperatingHours },
      });
      setData(resp?.data || null);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load classroom utilization report');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, appliedOperatingHours]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const applyOperatingHours = () => {
    const num = Number.parseFloat(operatingHoursInput);
    if (Number.isNaN(num) || num <= 0 || num > 24) {
      setError('Operating hours/day must be a number between 0 and 24.');
      return;
    }
    setAppliedOperatingHours(num);
  };

  const openDetails = useCallback((room, dayType) => {
    const params = new URLSearchParams({
      classroomId: String(room.classroomId),
      name: room.name || '',
      floor: room.floor || '',
      startDate,
      endDate,
      dayType,
      operatingHoursPerDay: String(appliedOperatingHours),
    });
    window.open(`/admin/report/classroom-utilization/detail?${params.toString()}`, '_blank', 'noopener,noreferrer');
  }, [startDate, endDate, appliedOperatingHours]);

  const handlePrint = () => {
    const params = new URLSearchParams({
      startDate,
      endDate,
      operatingHoursPerDay: String(appliedOperatingHours),
    });
    window.open(`/admin/report/classroom-utilization/print?${params.toString()}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <DashboardLayout title="Classroom Utilization Report">
      <div className="mx-auto max-w-6xl">
        <div className="mb-4 flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate('/admin/report/monitor')}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Monitor Report
          </button>
          <button
            type="button"
            onClick={handlePrint}
            className="inline-flex items-center gap-1.5 rounded-lg border border-indigo-600 bg-indigo-600 px-3 py-2 text-xs font-medium text-white hover:bg-indigo-700"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4H7v4a2 2 0 002 2z" />
            </svg>
            Print Official Report
          </button>
        </div>

        <div className="mb-6 rounded-xl border border-indigo-200 bg-indigo-50 p-6">
          <h2 className="flex items-center gap-2 text-base font-semibold text-indigo-800">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-2 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            Classroom Utilization Report
          </h2>
          <p className="mt-1 text-sm text-indigo-900">
            Room-wise usage for the selected period, split into weekday and weekend sessions (classified by each
            class's actual calendar date). Utilization % = booked hours &divide; available room-hours, where
            available hours assume {appliedOperatingHours}h of operating time per day.
          </p>

          <div className="mt-4 flex flex-wrap items-end gap-4">
            <div className="flex flex-col">
              <label className="mb-1 text-xs font-medium text-gray-700">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
              />
            </div>
            <div className="flex flex-col">
              <label className="mb-1 text-xs font-medium text-gray-700">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
              />
            </div>
            <div className="flex flex-col">
              <label className="mb-1 text-xs font-medium text-gray-700">Operating hours/day</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  min={0.5}
                  max={24}
                  step={0.5}
                  value={operatingHoursInput}
                  onChange={(e) => setOperatingHoursInput(e.target.value)}
                  className="w-24 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={applyOperatingHours}
                  className="rounded-lg border border-indigo-600 bg-indigo-600 px-3 py-2 text-xs font-medium text-white hover:bg-indigo-700"
                >
                  Apply
                </button>
              </div>
            </div>
            <button
              type="button"
              onClick={loadReport}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50"
            >
              Refresh
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">{error}</div>
        )}

        {loading ? (
          <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-gray-600">
            Loading classroom utilization…
          </div>
        ) : data ? (
          <>
            <BucketTable
              title="Weekday Utilization"
              bucket={data.weekday}
              dayCount={data.weekdayDayCount}
              operatingHoursPerDay={data.operatingHoursPerDay}
              dayType="weekday"
              onViewDetails={openDetails}
            />
            <BucketTable
              title="Weekend Utilization"
              bucket={data.weekend}
              dayCount={data.weekendDayCount}
              operatingHoursPerDay={data.operatingHoursPerDay}
              dayType="weekend"
              onViewDetails={openDetails}
            />
          </>
        ) : null}
      </div>
    </DashboardLayout>
  );
}
